<script setup lang="ts">
  import { Loader2, FileText } from 'lucide-vue-next'
  import {
    computed,
    onMounted,
    onUnmounted,
    onBeforeUnmount,
    inject,
    ref,
    type Ref,
    nextTick,
    watch
  } from 'vue'
  import SignModal from '@/components/pdf/SignModalV3.vue'
  import GenericPdfNav from '@/components/pdf/genericPdfNav.vue'
  import { downloadDocument } from '@/lib/utils'
  import { VuePDF } from '@tato30/vue-pdf'
  import { usePDFWithCmaps as usePDF } from '@/composables/usePDFWithCmaps'
  import { useSignatures } from '@/store/signatureStore'
  import { usePdfFormSubmissionStore } from '@/store/pdfFormSubmissionStore'
  import Button from '@/components/ui/button/Button.vue'
  import Header from '@/components/clientPortal/Header.vue'
  import '@tato30/vue-pdf/style.css'
  import CompletedPdf from '@/components/pdf/completedPdf.vue'
  import client404 from '@/components/404/404.vue'
  import { useRoute } from 'vue-router'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import {
    AssignedToEnum,
    type ClientCase,
    type ClientOrganisation,
    type PDFFormSubmissionBase,
    PdfFormSubmissionsService
  } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import ConfirmationDialog from '@/components/ui/confirmation-dialog/ConfirmationDialog.vue'
  import FormFieldOverlayItem from '@/components/forms/FormFieldOverlayItem.vue'
  import { usePdfFormValidation } from '@/composables/usePdfFormValidation'
  import { useToast } from '@/components/ui/toast'

  const { t } = useTranslation({
    en: {
      backRequiredActions: 'Back required actions',
      submitDocument: 'Submit Document',
      pageOf: 'of',
      loadingSubmission: 'Loading submission...',
      submissionNotFound: 'Submission not found',
      continueFilling: 'Continue Filling',
      downloadCompleted: 'Download Completed Form',
      zoomOut: 'Zoom out',
      zoomIn: 'Zoom in',
      fitToPage: 'Fit to page',
      previousPage: 'Previous page',
      nextPage: 'Next page',
      of: 'of',
      loadingPdf: 'Loading PDF...',
      pdfError: 'Error loading PDF. Please check the file and try again.',
      validationError: 'Please fill in all required fields correctly before submitting.',
      validationErrorTitle: 'Validation Error',
      validationErrorWithPage:
        'Please complete all required fields. First error found on page {page}.'
    },
    de: {
      backRequiredActions: 'Zurück zu erforderlichen Aktionen',
      submitDocument: 'Dokument abschicken',
      pageOf: 'von',
      loadingSubmission: 'Einreichung wird geladen...',
      submissionNotFound: 'Einreichung nicht gefunden',
      continueFilling: 'Weiter ausfüllen',
      downloadCompleted: 'Abgeschlossenes Formular herunterladen',
      zoomOut: 'Verkleinern',
      zoomIn: 'Vergrößern',
      fitToPage: 'An Seite anpassen',
      previousPage: 'Vorherige Seite',
      nextPage: 'Nächste Seite',
      of: 'von',
      loadingPdf: 'PDF wird geladen...',
      pdfError:
        'Fehler beim Laden der PDF. Bitte überprüfen Sie die Datei und versuchen Sie es erneut.',
      validationError:
        'Bitte füllen Sie alle erforderlichen Felder korrekt aus, bevor Sie das Formular absenden.',
      validationErrorTitle: 'Validierungsfehler',
      validationErrorWithPage:
        'Bitte füllen Sie alle erforderlichen Felder aus. Erster Fehler auf Seite {page}.'
    }
  })

  const signatureStore = useSignatures()
  const submissionStore = usePdfFormSubmissionStore()
  const themeColor = computed(() => useThemeColor().value)
  const { toast } = useToast()
  const width = ref<number>(window.innerWidth)
  const route = useRoute()
  const caseRecord = inject<Ref<ClientCase | null>>('caseRecord')
  const accessToken = inject<Ref<string | null>>('accessToken')
  const org = inject<Ref<ClientOrganisation | null>>('org')
  const submissionUuid = route.params.submission_uuid as string
  const pdfFormSubmission = ref<PDFFormSubmissionBase | null>(null)
  const isSubmitting = ref(false)

  const loading = ref(true)
  const error = computed(() => {
    if (loading.value) return null
    if (!pdfFormSubmission.value) return t('submissionNotFound')
    return null
  })

  // PDF viewer state
  const currentPage = ref(1)
  const totalPages = ref(1)
  const pdfLoading = ref(true)
  const pdfError = ref<string | null>(null)
  const isMobile = computed(() => width.value < 768)
  const zoomLevel = ref(1)
  const minZoom = 0.5
  const maxZoom = 3.0
  const fitPage = ref(true)
  const calculatedFitScale = ref(1)
  const pdfContainerRef = ref<HTMLElement | null>(null)
  const pdfInnerRef = ref<HTMLElement | null>(null)
  const isManualZoom = ref(false)

  // Form state
  const showSignatureModal = ref(false)
  const currentSignatureField = ref<any>(null)
  const signatureModalTriggerEl = ref<HTMLElement | null>(null)

  const isExternalRoute = computed(() => {
    return (
      pdfFormSubmission.value?.is_external ||
      !accessToken?.value ||
      route.path.includes('/external/')
    )
  })

  const isStandaloneRoute = computed(() => {
    return pdfFormSubmission.value?.is_standalone || route.path.includes('/standalone/')
  })

  const isCompleted = computed(() => {
    return pdfFormSubmission.value?.submission_status === 'completed'
  })

  const pdfUrl = ref('')
  const { pdf, pages } = usePDF(pdfUrl)

  const filename = computed(() => {
    return pdfFormSubmission.value?.template_name || 'PDF Form'
  })

  const templateFields = computed(() => {
    if (
      !pdfFormSubmission.value?.form_fields ||
      !Array.isArray(pdfFormSubmission.value.form_fields)
    )
      return []
    return pdfFormSubmission.value.form_fields
  })

  const fieldsForCurrentPage = computed(() => {
    return templateFields.value.filter((field) => field.coordinates.page === currentPage.value)
  })

  const effectiveZoom = computed(() => {
    if (fitPage.value) {
      return calculatedFitScale.value
    }
    return zoomLevel.value
  })

  const formData = computed(() => ({
    form_fields: submissionStore.getFormData()
  }))

  const getFieldValue = (fieldId: string | undefined) => {
    const value = submissionStore.getFieldValue(fieldId)
    const field = templateFields.value.find((f) => f.uuid === fieldId)

    if (field?.type === 'checkbox') {
      return value === 'true' || value === 'True' || value === 'TRUE'
    }

    if (field?.type === 'signature') {
      if (value) return value

      if (pdfFormSubmission.value?.uuid && fieldId) {
        const signatureKey = pdfFormSubmission.value.uuid + fieldId
        const signature = signatureStore.signatures[signatureKey]
        return signature?.data_uri || ''
      }
    }

    return value
  }

  const { validateSingleField, validateAllFields, getFieldError, clearErrors } =
    usePdfFormValidation(getFieldValue)

  const canSubmit = computed(() => {
    const currentPageFields = fieldsForCurrentPage.value

    for (const field of currentPageFields) {
      if (field.uuid && getFieldError(field.uuid)) {
        return false
      }
    }

    return true
  })

  async function handleSubmitForm() {
    if (isSubmitting.value) {
      return
    }
    if (!pdfFormSubmission.value) return

    const clientFields = templateFields.value.filter(
      (field) => field.assigned_to === AssignedToEnum.CLIENT
    )
    const isValid = validateAllFields(clientFields)
    if (!isValid) {
      const firstErrorField = clientFields.find((field) => field.uuid && getFieldError(field.uuid))
      const errorPage = firstErrorField?.coordinates?.page || 1

      toast({
        title: t('validationErrorTitle'),
        description: t('validationErrorWithPage', { page: errorPage.toString() }),
        variant: 'destructive'
      })

      if (firstErrorField?.coordinates?.page) {
        currentPage.value = firstErrorField.coordinates.page
      }

      return
    }

    isSubmitting.value = true
    try {
      pdfFormSubmission.value = (await PdfFormSubmissionsService.pdfFormSubmissionsPartialUpdate({
        uuid: pdfFormSubmission.value.uuid,
        requestBody: {
          submission_status: 'completed',
          form_fields: formData.value.form_fields
        }
      })) as PDFFormSubmissionBase

      // Clear the store after successful submission
      submissionStore.clearSubmission()
      clearErrors()
    } catch (err) {
      console.error('Error submitting form:', err)
    } finally {
      isSubmitting.value = false
    }
  }

  const updateFieldValue = (fieldId: string | undefined, value: any, fieldType?: string) => {
    if (!fieldId) return

    const field = templateFields.value.find((f) => f.uuid === fieldId)
    const type = fieldType || field?.type || 'text'

    submissionStore.updateFieldValue(fieldId, value, type)

    if (field) {
      validateSingleField(field)
    }
  }

  const handleSignatureClick = (field: any) => {
    const active = document.activeElement
    signatureModalTriggerEl.value =
      active && active !== document.body ? (active as HTMLElement) : null
    currentSignatureField.value = field
    // Set the signature store's selected field so SignModalV3 knows which field to update
    // SignModalV3 expects a field with 'name' property, so we use uuid as name
    signatureStore.selectedSignatureFieldObject = {
      ...field,
      name: field.uuid,
      id: field.uuid
    }
    showSignatureModal.value = true
    signatureStore.showSignModal = true
  }

  const restoreSignatureFocus = () => {
    nextTick(() => {
      signatureModalTriggerEl.value?.focus()
      signatureModalTriggerEl.value = null
    })
  }

  const getSignatureData = (field: any) => {
    if (field?.uuid) {
      const value = submissionStore.getFieldValue(field.uuid)
      if (value) return value
    }

    // Fallback to signatureStore (for compatibility)
    // SignModalV3 stores signatures using uuid as the name
    if (pdfFormSubmission.value?.uuid && field?.uuid) {
      const signatureKey = pdfFormSubmission.value.uuid + field.uuid
      const signature = signatureStore.signatures[signatureKey]
      return signature?.data_uri || ''
    }

    return ''
  }

  async function calculateFitScale() {
    if (!pdf.value || !fitPage.value) return

    try {
      let pdfDocument: any = pdf.value
      if (typeof pdfDocument.getPage !== 'function' && pdfDocument.promise) {
        pdfDocument = await pdfDocument.promise
      }
      if (!pdfDocument || typeof pdfDocument.getPage !== 'function') return

      const page = await pdfDocument.getPage(currentPage.value)
      const viewport = page.getViewport({ scale: 1.0 })

      const measureEl = pdfInnerRef.value || pdfContainerRef.value
      if (!measureEl) return

      const containerWidth = measureEl.clientWidth
      if (containerWidth <= 0) return

      const newScale = containerWidth / viewport.width
      if (Math.abs(newScale - calculatedFitScale.value) > 0.001) {
        calculatedFitScale.value = newScale
      }
    } catch {
      // PDF not ready yet, will recalculate on next @loaded
    }
  }

  const onPdfLoaded = (viewport: any, pageNumber?: number) => {
    pdfLoading.value = false
    pdfError.value = null

    if (pdf.value) {
      nextTick(() => {
        totalPages.value = pages.value || 1
      })
    }

    if (fitPage.value) {
      calculateFitScale()
    }
  }

  const onPdfError = () => {
    pdfLoading.value = false
    pdfError.value = 'Failed to load PDF'
  }

  const handleZoomChange = (zoomData: {
    zoomLevel: number
    fitPage: boolean
    isManualZoom: boolean
  }) => {
    zoomLevel.value = zoomData.zoomLevel
    fitPage.value = zoomData.fitPage
    isManualZoom.value = zoomData.isManualZoom
  }

  const handleFitToPage = () => {
    fitPage.value = true
    isManualZoom.value = false
    calculateFitScale()
  }

  onMounted(async () => {
    try {
      pdfFormSubmission.value = (await PdfFormSubmissionsService.pdfFormSubmissionsRetrieve({
        uuid: submissionUuid
      })) as PDFFormSubmissionBase

      // Set org data from submission for theme styling
      if (pdfFormSubmission.value?.org && org) {
        org.value = pdfFormSubmission.value.org
      }

      signatureStore.setPdfStore('new')

      if (pdfFormSubmission.value?.uuid) {
        signatureStore.setManualFormId(pdfFormSubmission.value.uuid)

        // Initialize the submission store with form fields
        if (pdfFormSubmission.value.form_fields) {
          submissionStore.initializeSubmission(
            pdfFormSubmission.value.uuid,
            pdfFormSubmission.value.form_fields
          )

          // Sync with signature store to load any existing signatures
          submissionStore.syncWithSignatureStore(
            signatureStore,
            pdfFormSubmission.value.uuid,
            pdfFormSubmission.value.form_fields
          )
        }
      }
    } catch (err) {
      console.error('Error loading submission:', err)
    } finally {
      loading.value = false
    }

    window.addEventListener('resize', () => (width.value = window.innerWidth))
  })

  onBeforeUnmount(() => {
    signatureStore.cleanup()
  })

  onUnmounted(() => {
    window.removeEventListener('resize', () => (width.value = window.innerWidth))
    signatureStore.clearSignatures()
  })

  function handleDownloadClick() {
    if (!pdfFormSubmission.value) {
      return
    }

    if (!pdfUrl.value) {
      return
    }

    downloadDocument(pdfUrl.value, `${filename.value}_completed.pdf`)
  }

  watch(
    pages,
    (newPages) => {
      if (newPages) {
        totalPages.value = newPages
      }
    },
    { immediate: true }
  )

  watch(
    [pdf, pages],
    ([newPdf, newPages]) => {
      if (newPdf && newPages && newPages > 0) {
        pdfLoading.value = false
        pdfError.value = null
      } else if (!newPdf && pdfUrl.value) {
        pdfLoading.value = true
        pdfError.value = null
      }
    },
    { immediate: true }
  )

  // Update PDF URL when submission loads or completion status changes
  watch(
    () => pdfFormSubmission.value,
    (submission) => {
      if (!submission) return

      // If completed and has flattened PDF, use the flattened version
      if (submission.flattened_pdf) {
        pdfUrl.value = submission.flattened_pdf
      } else {
        // Otherwise use the processed PDF (empty template for filling)
        pdfUrl.value = submission.processed_pdf || ''
      }

      if (pdfUrl.value) {
        calculatedFitScale.value = 1
        fitPage.value = true
        pdfLoading.value = true
        pdfError.value = null
      }
    },
    { immediate: true }
  )

  watch(
    () => signatureStore.signatures,
    () => {
      if (pdfFormSubmission.value?.uuid && pdfFormSubmission.value.form_fields) {
        submissionStore.syncWithSignatureStore(
          signatureStore,
          pdfFormSubmission.value.uuid,
          pdfFormSubmission.value.form_fields
        )
      }
    },
    { deep: true }
  )

  watch(width, () => {
    if (fitPage.value) {
      nextTick(() => calculateFitScale())
    }
  })
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Loading State -->
    <div
      v-if="loading"
      class="flex h-screen items-center justify-center"
      role="status"
    >
      <div class="flex flex-col items-center gap-3">
        <Loader2
          class="h-8 w-8 animate-spin text-gray-600"
          aria-hidden="true"
        />
        <div class="text-center">
          <div class="text-sm font-medium text-gray-600">{{ t('loadingSubmission') }}</div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="min-h-screen bg-gradient-to-bl from-light-cyan via-white to-light-cyan py-4"
      role="alert"
    >
      <client404 />
    </div>

    <!-- Main Content -->
    <div
      v-else-if="pdfFormSubmission"
      class="min-h-screen"
    >
      <!-- Check if the submission is completed -->
      <CompletedPdf
        v-if="isCompleted"
        :is-standalone="isStandaloneRoute"
        :is-external="isExternalRoute"
        :handleDownloadClick="handleDownloadClick"
        :document-title="filename"
      />

      <!-- Display the form for filling/editing -->
      <div
        v-else
        class="pdf-form-view-container flex flex-col"
      >
        <Header
          :title="filename"
          :show-back-button="!isExternalRoute && !isStandaloneRoute"
          :is-external-route="isExternalRoute || isStandaloneRoute"
          :full-width="true"
          back-button-data-cy="ClientPortalPDFFormViewMobileBackButton"
          title-data-cy="ClientPortalPDFFormViewMobileTitle"
        />

        <!-- Main Content Area with top padding for fixed header -->
        <div
          class="pdf-form-content-area flex-1 overflow-y-auto overflow-x-hidden"
          :class="isExternalRoute || isStandaloneRoute ? 'pt-[64px]' : 'pt-[84px]'"
        >
          <div class="flex h-full flex-col">
            <div
              ref="pdfContainerRef"
              class="flex-1 overflow-auto px-3 py-6 md:px-6"
            >
              <div class="mx-auto flex w-full max-w-4xl justify-center">
                <div class="pdf-form-wrapper relative w-full">
                  <div
                    v-if="pdfLoading && pdfUrl"
                    class="flex min-h-[800px] w-full items-center justify-center rounded-lg border border-gray-200 bg-white
                      shadow-lg md:min-w-[600px]"
                    role="status"
                  >
                    <div class="text-center">
                      <div
                        class="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"
                        aria-hidden="true"
                      ></div>
                      <p class="text-gray-500">{{ t('loadingPdf') }}</p>
                    </div>
                  </div>

                  <div
                    v-else-if="pdfError"
                    class="flex min-h-[800px] items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg"
                    aria-live="polite"
                  >
                    <div class="text-center">
                      <p class="mb-2 text-red-500">{{ pdfError }}</p>
                      <p class="text-sm text-gray-500">{{ t('pdfError') }}</p>
                    </div>
                  </div>

                  <div
                    v-else-if="pdf && pdfUrl"
                    ref="pdfInnerRef"
                    class="relative mx-auto w-full rounded-lg border border-gray-200 bg-white shadow-lg"
                    :class="{ 'max-w-fit': !isMobile }"
                  >
                    <VuePDF
                      :pdf="pdf"
                      :page="currentPage"
                      :scale="effectiveZoom"
                      class="pdf-no-select w-full"
                      @loaded="(viewport) => onPdfLoaded(viewport, currentPage)"
                      @error="onPdfError"
                    />

                    <FormFieldOverlayItem
                      v-for="field in fieldsForCurrentPage"
                      :key="`field-${field.uuid}-${currentPage}`"
                      :field="field"
                      :zoom-level="effectiveZoom"
                      :is-selected="false"
                      :is-editable="false"
                      :is-fillable="true"
                      :field-value="getFieldValue(field.uuid)"
                      :signature-data="field.type === 'signature' ? getSignatureData(field) : ''"
                      :validation-error="getFieldError(field.uuid)"
                      is-filled-by="client"
                      @field-value-change="updateFieldValue"
                      @signature-click="handleSignatureClick"
                    />
                  </div>

                  <div
                    v-else
                    class="flex min-h-[800px] items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg"
                  >
                    <div class="text-center text-gray-500">
                      <FileText class="mx-auto mb-4 h-12 w-12 text-gray-300" />
                      <p>No PDF available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Fixed Bottom Navigation Bar -->
        <div class="pdf-form-bottom-controls border-t border-gray-200 bg-white shadow-lg">
          <div class="flex items-center justify-between gap-2 p-3 md:p-4">
            <GenericPdfNav
              :current-page="currentPage"
              :total-pages="totalPages"
              :theme-color="themeColor"
              :show-zoom-controls="!isMobile"
              :zoom-level="zoomLevel"
              :fit-page="fitPage"
              :is-manual-zoom="isManualZoom"
              :min-zoom="minZoom"
              :max-zoom="maxZoom"
              @update-page="(page) => (currentPage = page)"
              @zoom-change="handleZoomChange"
              @fit-to-page="handleFitToPage"
            />

            <div class="flex gap-2">
              <ConfirmationDialog
                v-if="!isCompleted"
                type="document"
                :mobile-bottom-sheet="true"
                :disabled="!canSubmit || isSubmitting"
                data-cy="clientPdfFormViewSubmitButton"
                @confirm="handleSubmitForm"
              >
                <template #trigger>
                  <Button
                    :class="`bg-${themeColor}-600 hover:bg-${themeColor}-700`"
                    :disabled="!canSubmit || isSubmitting"
                  >
                    {{ t('submitDocument') }}
                    <Loader2
                      v-if="isSubmitting"
                      class="ml-2 h-4 w-4 animate-spin"
                    />
                  </Button>
                </template>
              </ConfirmationDialog>

              <!-- Download Button (if completed) -->
              <Button
                v-if="isCompleted"
                :class="`bg-${themeColor}-600 hover:bg-${themeColor}-700`"
                @click="handleDownloadClick"
              >
                {{ t('downloadCompleted') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fallback for no submission -->
    <div
      v-else
      class="min-h-screen bg-gradient-to-bl from-light-cyan via-white to-light-cyan py-4"
    >
      <client404 />
    </div>
    <div id="confirm-modal"></div>

    <!-- Signature Modal -->
    <SignModal
      v-if="showSignatureModal && currentSignatureField"
      :data="getSignatureData(currentSignatureField)"
      :z-index="100"
      @close="
        () => {
          showSignatureModal = false
          signatureStore.showSignModal = false
          restoreSignatureFocus()
        }
      "
      @save="
        (signatureData: any) => {
          updateFieldValue(currentSignatureField.uuid, signatureData)
          showSignatureModal = false
          signatureStore.showSignModal = false
          restoreSignatureFocus()
        }
      "
    />
  </div>
</template>

<style scoped>
  input[type='text'],
  textarea {
    border: 1px solid #e5e7eb !important;
    border-radius: 0;
    padding: 0.5rem;
    font-size: 1rem;
    width: 100%;
  }

  /* Disable text selection on PDF */
  .pdf-no-select {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .pdf-no-select * {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  /* Desktop layout - standard flow */
  @media (min-width: 768px) {
    .pdf-form-view-container {
      min-height: 100vh;
    }

    .pdf-form-content-area {
      padding-bottom: 5rem;
    }

    .pdf-form-bottom-controls {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 50;
    }
  }

  /* Mobile layout - use viewport units and flexbox */
  @media (max-width: 767px) {
    .pdf-form-view-container {
      height: 100vh;
      height: 100dvh; /* Use dynamic viewport height for mobile browsers */
      overflow: hidden; /* fallback for iOS Safari < 16 */
      overflow: clip;
    }

    .pdf-form-content-area {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      /* Add padding for safe area on mobile devices */
      padding-bottom: 1rem;
    }

    .pdf-form-wrapper {
      max-width: 100vw;
      box-sizing: border-box;
    }

    /* Prevent PDF layers from overflowing container on mobile.
       Do NOT force width:100% on canvas — the scale calculation in onPdfLoaded
       sizes the canvas to fit, and forcing width would create a mismatch
       between the visual canvas size and overlay coordinate space. */
    .pdf-form-wrapper :deep(canvas) {
      max-width: 100% !important;
      height: auto !important;
    }

    .pdf-form-wrapper :deep(.textLayer),
    .pdf-form-wrapper :deep(.annotationLayer) {
      max-width: 100% !important;
    }

    .pdf-form-wrapper :deep(.vue-pdf-embed) {
      max-width: 100% !important;
      overflow: hidden !important;
      overflow: clip !important;
    }

    /* Bottom controls - not fixed on mobile, part of flexbox flow */
    .pdf-form-bottom-controls {
      flex-shrink: 0;
      /* Add safe area inset for devices with notches/home indicators */
      padding-bottom: env(safe-area-inset-bottom, 0px);
    }

    /* Make bottom controls more compact on mobile */
    .pdf-form-bottom-controls :deep(button) {
      padding: 0.5rem 0.75rem;
      font-size: 0.875rem;
    }

    /* Ensure submit button doesn't wrap */
    .pdf-form-bottom-controls .flex.gap-2 {
      flex-shrink: 0;
    }
  }
</style>
